/**
 * Time Tracking API client — typed functions for time entries.
 */
import { apiClient } from './index';

export interface TimeEntry {
  id: string;
  tenant_id: string;
  task_id: string;
  user_id: string;
  hours: number;
  seconds: number; // exact elapsed; hours is the rounded mirror
  note: string;
  logged_at: string; // YYYY-MM-DD
  created_at: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

export interface TimeEntriesResponse {
  data: TimeEntry[];
  total_hours: number;
}

/** A running stopwatch for the current user on a task. */
export interface TimerSession {
  id: string;
  tenant_id: string;
  user_id: string;
  task_id: string;
  started_at: string; // RFC3339
  created_at: string;
  task_title?: string;
}

export async function listTimeEntries(taskId: string): Promise<TimeEntriesResponse> {
  const { data } = await apiClient.get<TimeEntriesResponse>(`/tasks/${taskId}/time-entries`);
  return data;
}

export async function logTime(
  taskId: string,
  input: { hours: number; note?: string; logged_at?: string },
): Promise<TimeEntry> {
  const { data } = await apiClient.post<TimeEntry>(`/tasks/${taskId}/time-entries`, input);
  return data;
}

export async function deleteTimeEntry(taskId: string, entryId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/time-entries/${entryId}`);
}

// ─── Live timer ────────────────────────────────────────────────────────────

/** The current user's running timer across all tasks, or null if none. */
export async function getActiveTimer(): Promise<TimerSession | null> {
  const { data } = await apiClient.get<{ data: TimerSession | null }>('/me/active-timer');
  return data.data;
}

/** Start a timer on a task (auto-stops any prior running timer). */
export async function startTimer(taskId: string): Promise<TimerSession> {
  const { data } = await apiClient.post<TimerSession>(`/tasks/${taskId}/timer/start`);
  return data;
}

/** Stop the running timer on a task; returns the recorded time entry. */
export async function stopTimer(taskId: string): Promise<TimeEntry> {
  const { data } = await apiClient.post<TimeEntry>(`/tasks/${taskId}/timer/stop`);
  return data;
}

/** Cancel a running timer without recording any time. */
export async function discardTimer(taskId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/timer`);
}
